import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import ConfirmationModal from '../../components/admin/ConfirmationModal';
import AddUserModal from '../../components/admin/AddUserModal';
import EditUserModal from '../../components/admin/EditUserModal';

import { buildApiUrl } from '../../config/api';
import { fetchJson, formatFetchError } from '../../utils/fetchJson';
import { useSocket } from '../../context/SocketContext';
import {
  FiUsers,
  FiSearch,
  FiEye,
  FiTrash2,
  FiMail,
  FiShield,
  FiStar,
  FiUserX,
  FiUserCheck,
  FiUserPlus,
  FiRefreshCw,
  FiDownload,
  FiMapPin,
  FiCalendar,
  FiBriefcase,
  FiPhone,
  FiGlobe,
  FiEdit,
  FiCheck,
  FiCopy,
  FiX
} from 'react-icons/fi';
import { buildMediaUrl } from '../../config/api';
import { useToast } from '../../context/ToastContext';

const UserManagement = () => {
  const { onlineUsers } = useSocket();
  const { success, error: showError } = useToast();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    newToday: 0,
    premium: 0,
    googlePlay: 0,
    verified: 0,
    suspended: 0
  });
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false); // New local loading state
  const [statsLoading, setStatsLoading] = useState(true);
  const [searchInput, setSearchInput] = useState(() => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.get('search') || '';
    } catch {
      return '';
    }
  });
  const [searchTerm, setSearchTerm] = useState(() => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.get('search') || '';
    } catch {
      return '';
    }
  });
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterMembership, setFilterMembership] = useState('all');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showUserDetails, setShowUserDetails] = useState(null); // Used to control modal visibility
  const [detailedUser, setDetailedUser] = useState(null); // Holds the full fetched user data
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [filterCountry, setFilterCountry] = useState('all');
  const [countries, setCountries] = useState([]);
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    action: null, // 'suspend', 'delete', 'verify', 'unsuspend', 'unverify', 'message'
    data: null, // userId or array of userIds
    isBulk: false,
    inputValue: ''
  });
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [userToEdit, setUserToEdit] = useState(null);
  const [copiedOrderId, setCopiedOrderId] = useState(false);

  const itemsPerPage = 10;

  const fetchUsers = useCallback(async () => {


    try {
      const token = localStorage.getItem('token');

      // Build query string
      const params = new URLSearchParams();
      params.set('page', String(currentPage));
      params.set('limit', String(itemsPerPage));
      if (searchTerm) params.set('search', searchTerm);
      if (filterStatus !== 'all') params.set('status', filterStatus);
      if (filterMembership !== 'all') params.set('membership', filterMembership);
      if (filterCountry !== 'all') params.set('country', filterCountry);

      const { response, data } = await fetchJson(buildApiUrl(`/api/admin/users?${params}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok && data.success) {
        const fetchedUsers = Array.isArray(data?.data?.users) ? data.data.users : [];
        setUsers(fetchedUsers);
        setTotalUsers(data.data.pagination.total);
        setTotalPages(data.data.pagination.pages);
      } else {
        console.error('API Error:', data?.message);
        setUsers([]);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]);
      alert(formatFetchError(error, 'Failed to load users.'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage, searchTerm, filterStatus, filterMembership, filterCountry]);

  const fetchCountries = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const { response, data } = await fetchJson(buildApiUrl('/api/admin/users/countries'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok && data.success) {
        setCountries(data.data);
      }
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    fetchCountries();
  }, [fetchCountries]);

  const fetchUserStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const { response, data } = await fetchJson(buildApiUrl('/api/admin/stats'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok && data.success) {
        setStats({
          total: data.data.totalUsers || 0,
          active: data.data.activeUsers || 0,
          newToday: data.data.newUsersToday || 0,
          premium: data.data.premiumUsers || 0,
          googlePlay: data.data.googlePlaySubscribers || 0,
          verified: data.data.verifiedUsers || 0,
          suspended: data.data.suspendedUsers || 0
        });
      }
    } catch (error) {
      console.error('Error fetching user stats:', error);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUserStats();
  }, [fetchUserStats]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  // Automatically search as user types with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      const trimmed = searchInput.trim();
      if (trimmed !== searchTerm) {
        setSearchTerm(trimmed);
        setCurrentPage(1);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [searchInput, searchTerm]);

  const handleSearch = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setCurrentPage(1); // Reset to first page on new search
    setSearchTerm(searchInput.trim());
  };

  const refreshData = () => {
    fetchUsers();
    fetchUserStats();
    setSelectedUsers([]);
  };

  const openConfirmationModal = (action, data, isBulk = false) => {
    let title = 'Confirm Action';
    let message = 'Are you sure you want to proceed?';
    let count = isBulk ? data.length : 1;

    switch (action) {
      case 'suspend':
        title = `Suspend ${isBulk ? 'Users' : 'User'}`;
        message = `Are you sure you want to suspend ${count} user${count > 1 ? 's' : ''}? They will not be able to log in.`;
        break;
      case 'unsuspend':
        title = `Unsuspend ${isBulk ? 'Users' : 'User'}`;
        message = `Are you sure you want to reactivate ${count} user${count > 1 ? 's' : ''}?`;
        break;
      case 'delete':
        title = `Delete ${isBulk ? 'Users' : 'User'}`;
        message = `Are you sure you want to PERMANENTLY DELETE ${count} user${count > 1 ? 's' : ''}? This action cannot be undone and all user data will be lost.`;
        break;
      case 'verify':
        title = `Verify ${isBulk ? 'Users' : 'User'}`;
        message = `Mark ${count} user${count > 1 ? 's' : ''} as verified?`;
        break;
      case 'unverify':
        title = `Unverify ${isBulk ? 'Users' : 'User'}`;
        message = `Remove verification status from ${count} user${count > 1 ? 's' : ''}?`;
        break;
      case 'message':
        title = `Send Message to ${isBulk ? 'Users' : 'User'}`;
        message = isBulk
          ? `Send a message to ${count} users:`
          : 'Enter your message below:';
        break;
      default:
        break;
    }

    setConfirmationModal({
      isOpen: true,
      title,
      message,
      action,
      data,
      isBulk,
      inputValue: ''
    });
  };

  const handleConfirmAction = async () => {
    const { action, data, isBulk } = confirmationModal;
    setActionLoading(true); // User local loading state

    try {
      const token = localStorage.getItem('token');
      const userIds = isBulk ? data : [data];

      // We'll perform actions sequentially or parallel. Parallel is faster.
      const promises = userIds.map(userId => {
        let url = '';
        let options = {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          method: 'POST'
        };

        switch (action) {
          case 'suspend':
            url = buildApiUrl(`/api/admin/users/${userId}/suspend`);
            options.body = JSON.stringify({ reason: 'Admin action' });
            break;
          case 'unsuspend':
            url = buildApiUrl(`/api/admin/users/${userId}/unsuspend`);
            break;
          case 'delete':
            url = buildApiUrl(`/api/admin/users/${userId}/delete`);
            break;
          case 'verify':
            url = buildApiUrl(`/api/admin/users/${userId}/verify`);
            break;
          case 'unverify':
            url = buildApiUrl(`/api/admin/users/${userId}/unverify`);
            break;
          case 'message':
            url = buildApiUrl(`/api/admin/users/${userId}/message`);
            options.body = JSON.stringify({ message: confirmationModal.inputValue });
            break;
          default:
            return Promise.resolve({ ok: true }); // No-op
        }

        return fetchJson(url, options);
      });

      await Promise.all(promises);

      // We assume success for simplicity in bulk, or you could track errors
      // For a better UX, we'd report partial failures, but for now we refresh.

      setConfirmationModal({ ...confirmationModal, isOpen: false });
      setSelectedUsers([]);

      // Only refresh data if the action changes user state (not for messaging)
      if (action !== 'message') {
        fetchUsers();
        fetchUserStats();
      } else {
        success('Message sent successfully!');
      }

      // Optional: Show success toast/alert (non-blocking)
      // alert(`Action ${action} completed successfully.`); 

    } catch (error) {
      console.error(`Error performing ${action}:`, error);
      showError(formatFetchError(error, `Failed to perform ${action}`));
    } finally {
      setActionLoading(false);
    }
  };

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return null;
    const birthDate = new Date(dateOfBirth);
    if (Number.isNaN(birthDate.getTime())) return null;
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const getProfilePhotoUrl = (photo) => {
    if (!photo) return null;
    if (photo.startsWith('http')) return photo;
    return buildMediaUrl(photo);
  };

  const handleViewDetails = async (user) => {
    setShowUserDetails(user);
    setDetailedUser(null);
    setLoadingDetails(true);

    try {
      const token = localStorage.getItem('token');
      // Try to fetch full profile details
      // We use the public profile endpoint as it likely contains the rich profile data
      const { response, data } = await fetchJson(buildApiUrl(`/api/user-profiles/${user._id || user.id}`), {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok && data.success) {
        setDetailedUser({ ...user, ...data.user });
      } else {
        // Fallback to basic info if profile fetch fails
        setDetailedUser(user);
      }
    } catch (error) {
      console.error('Error fetching user details:', error);
      setDetailedUser(user);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleDownloadPdf = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(buildApiUrl(`/api/admin/users/${userId}/pdf`), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to generate PDF');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `user_forensic_report_${userId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      success('PDF report downloaded successfully');
    } catch (err) {
      console.error('Error downloading PDF:', err);
      showError('Failed to generate and download user forensic PDF report.');
    }
  };

  const handleUserAction = (action, userId) => {

    openConfirmationModal(action, userId, false);
  };

  const handleBulkAction = (action) => {
    if (selectedUsers.length === 0) return;
    openConfirmationModal(action, selectedUsers, true);
  };

  const handleSelectUser = (userId) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleUserAdded = () => {
    fetchUsers();
    fetchUserStats();
  };



  const handleExportUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      // Fetch all users for export (with current filters)
      const params = new URLSearchParams();
      params.set('limit', '10000'); // Get all matching users
      if (searchTerm) params.set('search', searchTerm);
      if (filterStatus !== 'all') params.set('status', filterStatus);
      if (filterMembership !== 'all') params.set('membership', filterMembership);

      const response = await fetch(buildApiUrl(`/api/admin/users?${params}`), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success && data.data && data.data.users) {
        const usersToExport = data.data.users;

        // Convert to CSV
        const headers = ['ID', 'First Name', 'Last Name', 'Email', 'Phone', 'Gender', 'Status', 'Verified', 'Membership', 'Registered Date'];
        const csvContent = [
          headers.join(','),
          ...usersToExport.map(user => [
            user._id,
            `"${user.firstName || ''}"`,
            `"${user.lastName || ''}"`,
            `"${user.email || ''}"`,
            `"${user.phone || ''}"`,
            user.gender || '',
            user.status || '',
            user.verified ? 'Yes' : 'No',
            user.membershipType || '',
            new Date(user.createdAt).toLocaleDateString()
          ].join(','))
        ].join('\n');

        // Create download link
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `users_export_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error exporting users:', error);
      alert('Failed to export users.');
    }
  };

  const getOnlineBadge = (isOnline) => {
    return (
      <span
        className={`px-2 py-1 text-xs font-medium rounded-full ${isOnline ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
          }`}
      >
        {isOnline ? 'online' : 'offline'}
      </span>
    );
  };

  // Define filteredUsers based on search and filters
  const filteredUsers = React.useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return users.filter(user => {
      let matchesSearch = true;
      if (term) {
        const firstName = String(user.firstName || '').toLowerCase();
        const lastName = String(user.lastName || '').toLowerCase();
        const fullName = `${firstName} ${lastName}`.trim();
        const reverseFullName = `${lastName} ${firstName}`.trim();
        const name = String(user.name || '').toLowerCase();
        const customFullName = String(user.fullName || '').toLowerCase();
        const username = String(user.username || '').toLowerCase();
        const email = String(user.email || '').toLowerCase();
        const phone = String(user.phone || '').toLowerCase();
        const orderId = String(user.subscription?.orderId || '').toLowerCase();

        // 1. Direct substring match against full name, parts, or other identifiers
        matchesSearch =
          firstName.includes(term) ||
          lastName.includes(term) ||
          fullName.includes(term) ||
          reverseFullName.includes(term) ||
          name.includes(term) ||
          customFullName.includes(term) ||
          username.includes(term) ||
          email.includes(term) ||
          phone.includes(term) ||
          orderId.includes(term);

        // 2. Multi-word search (e.g. "John Doe") where every word matches any identifier
        if (!matchesSearch) {
          const words = term.split(/\s+/).filter(Boolean);
          if (words.length > 1) {
            matchesSearch = words.every(w =>
              firstName.includes(w) ||
              lastName.includes(w) ||
              fullName.includes(w) ||
              reverseFullName.includes(w) ||
              name.includes(w) ||
              customFullName.includes(w) ||
              username.includes(w) ||
              email.includes(w) ||
              phone.includes(w)
            );
          }
        }
      }

      const matchesStatus = filterStatus === 'all' || user.status === filterStatus;
      
      const matchesMembership = filterMembership === 'all' ||
        (filterMembership === 'google_play' && (user.subscription?.source === 'google_play' && (user.membershipType === 'premium' || user.membershipType === 'vip'))) ||
        (filterMembership === 'premium' && (user.membershipType === 'premium' || user.membershipType === 'vip')) ||
        (filterMembership === 'free' && (user.membershipType === 'free' || !user.membershipType)) ||
        user.membershipType === filterMembership;

      return matchesSearch && matchesStatus && matchesMembership;
    });
  }, [users, searchTerm, filterStatus, filterMembership]);

  // User statistics cards data
  const statCards = [
    {
      title: 'Total Users',
      value: stats.total,
      icon: <FiUsers className="w-6 h-6 text-blue-500" />,
      change: 12,
      changeType: 'increase'
    },
    {
      title: 'Active Users',
      value: stats.active,
      icon: <FiUserCheck className="w-6 h-6 text-green-500" />,
      change: 5,
      changeType: 'increase'
    },
    {
      title: 'New Today',
      value: stats.newToday,
      icon: <FiUserPlus className="w-6 h-6 text-purple-500" />,
      change: 3,
      changeType: 'increase'
    },
    {
      title: 'Premium Users',
      value: stats.premium,
      icon: <FiStar className="w-6 h-6 text-yellow-500" />,
      subtext: stats.googlePlay > 0 ? `${stats.googlePlay} via Google Play` : 'All platforms',
      change: stats.googlePlay,
      changeType: 'increase'
    }
  ];

  const handleSelectAll = () => {
    if (selectedUsers.length === filteredUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers.map(user => user._id || user.id));
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-gray-100 text-gray-800',
      deactivated: 'bg-gray-100 text-gray-800',
      suspended: 'bg-red-100 text-red-800',
      unverified: 'bg-yellow-100 text-yellow-800',
      pending: 'bg-yellow-100 text-yellow-800'
    };

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${styles[status] || styles.pending}`}>
        {status}
      </span>
    );
  };

  const getMembershipBadge = (type, subscription) => {
    if (type === 'premium' || type === 'vip') {
      const isGooglePlay = subscription?.source === 'google_play';
      const orderSnippet = subscription?.orderId ? ` (${subscription.orderId.slice(0, 8)}...)` : '';
      return (
        <div className="flex flex-col items-start gap-1">
          <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800 flex items-center gap-1 border border-purple-200 shadow-sm">
            <span>⭐</span>
            <span>{type === 'vip' ? 'VIP' : 'Premium'}</span>
          </span>
          {isGooglePlay && (
            <span className="text-[10px] font-medium text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-200" title={`Google Play: ${subscription?.orderId || 'Active'}`}>
              Google Play{orderSnippet}
            </span>
          )}
        </div>
      );
    }

    return (
      <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
        Free
      </span>
    );
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">User Management</h1>
            <p className="text-sm text-gray-500">Manage and monitor all user accounts</p>
          </div>
          <div className="flex space-x-2 mt-4 md:mt-0">
            <button
              onClick={refreshData}
              className="px-3 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center"
            >
              <FiRefreshCw className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={() => setShowAddUserModal(true)}
              className="px-4 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700 flex items-center"
            >
              <FiUserPlus className="mr-2" />
              Add User
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        {!statsLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {statCards.map((stat, index) => (
              <div key={index} className="bg-white p-4 rounded-lg shadow">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-500">{stat.title}</p>
                    <p className="text-2xl font-bold text-gray-800">{(stat.value ?? 0).toLocaleString()}</p>
                    <div className={`flex items-center text-sm mt-1 ${stat.changeType === 'increase' ? 'text-green-600' : 'text-red-600'
                      }`}>
                      {stat.changeType === 'increase' ? (
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        </svg>
                      )}
                      {stat.change}% from last week
                    </div>
                  </div>
                  <div className="p-3 rounded-full bg-opacity-10 bg-blue-100">
                    {stat.icon}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white p-4 rounded-lg shadow">
                <div className="animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
                  <div className="h-8 bg-gray-200 rounded w-1/2 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/3"></div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Search and Filter */}
        <div className="bg-white rounded-lg shadow overflow-hidden mb-6">
          <div className="p-4 border-b border-gray-200">
            <form onSubmit={handleSearch} className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0 md:space-x-4">
              <div className="relative flex-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiSearch className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-red-500 focus:border-red-500 sm:text-sm"
                  placeholder="Search by name, email, or phone..."
                  value={searchInput}
                  onChange={(e) => {
                    const val = e.target.value;
                    setSearchInput(val);
                    if (val === '') {
                      setSearchTerm('');
                      setCurrentPage(1);
                    }
                  }}
                />
                {searchInput && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchInput('');
                      setSearchTerm('');
                      setCurrentPage(1);
                    }}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none"
                    title="Clear search"
                  >
                    <FiX className="h-5 w-5" />
                  </button>
                )}
              </div>

              <div className="flex flex-wrap gap-2">
                <select
                  className="block w-full md:w-auto pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm rounded-md"
                  value={filterStatus}
                  onChange={(e) => {
                    setFilterStatus(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="suspended">Suspended</option>
                  <option value="unverified">Unverified</option>
                </select>

                <select
                  className="block w-full md:w-auto pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm rounded-md"
                  value={filterMembership}
                  onChange={(e) => {
                    setFilterMembership(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="all">All Memberships</option>
                  <option value="free">Free Members</option>
                  <option value="premium">⭐ Premium Members</option>
                  <option value="google_play">📱 Google Play Premium</option>
                </select>

                <select
                  className="block w-full md:w-auto pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm rounded-md"
                  value={filterCountry}
                  onChange={(e) => {
                    setFilterCountry(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="all">All Countries</option>
                  {countries.map((c) => (
                    <option key={c.country} value={c.country}>
                      {c.country} ({c.count})
                    </option>
                  ))}
                </select>

                <button
                  type="submit"
                  className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  Apply Filters
                </button>

                <button
                  type="button"
                  onClick={handleExportUsers}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center"
                >
                  <FiDownload className="w-4 h-4 mr-2" />
                  Export
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Bulk Actions */}
        {selectedUsers.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-blue-800">
                {selectedUsers.length} user{selectedUsers.length !== 1 ? 's' : ''} selected
              </span>
              <div className="flex gap-2">
                {/* Dynamically show Verify or Unverify based on selection?
                    For bulk, we'll keep it simple: Verify/Unverify explicit buttons or smart toggle?
                    User request: "suspend button convert to unsuspend".
                    Logic: If ALL selected are suspended, show "Unsuspend". Else "Suspend".
                 */}
                {selectedUsers.every(id => users.find(u => (u._id || u.id) === id)?.status === 'suspended') ? (
                  <button
                    onClick={() => handleBulkAction('unsuspend')}
                    className="px-3 py-1 bg-yellow-600 text-white rounded hover:bg-yellow-700 transition-colors text-sm"
                  >
                    Unsuspend
                  </button>
                ) : (
                  <button
                    onClick={() => handleBulkAction('suspend')}
                    className="px-3 py-1 bg-yellow-600 text-white rounded hover:bg-yellow-700 transition-colors text-sm"
                  >
                    Suspend
                  </button>
                )}

                {selectedUsers.every(id => users.find(u => (u._id || u.id) === id)?.verified) ? (
                  <button
                    onClick={() => handleBulkAction('unverify')}
                    className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 transition-colors text-sm"
                  >
                    Unverify
                  </button>
                ) : (
                  <button
                    onClick={() => handleBulkAction('verify')}
                    className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 transition-colors text-sm"
                  >
                    Verify
                  </button>
                )}

                <button
                  onClick={() => handleBulkAction('delete')}
                  className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 transition-colors text-sm"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Users Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <input
                      type="checkbox"
                      checked={filteredUsers.length > 0 && selectedUsers.length === filteredUsers.length}
                      onChange={handleSelectAll}
                      className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Account
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Online
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Membership
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Activity
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Reports
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map((user) => (
                  <tr key={user._id || user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedUsers.includes(user._id || user.id)}
                        onChange={() => handleSelectUser(user._id || user.id)}
                        className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 flex-shrink-0">
                          <div className="h-10 w-10 rounded-full bg-gradient-to-r from-red-500 to-pink-500 flex items-center justify-center text-white font-semibold">
                            {(user.firstName || '').charAt(0)}{(user.lastName || '').charAt(0)}
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {user.firstName} {user.lastName}
                          </div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                          <div className="text-sm text-gray-500">{user.phone || '—'}</div>
                          {user.verified && (
                            <div className="flex items-center mt-1">
                              <FiShield className="w-3 h-3 text-blue-500 mr-1" />
                              <span className="text-xs text-blue-500">Verified</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(user.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getOnlineBadge(onlineUsers?.has(String(user._id || user.id)))}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getMembershipBadge(user.membershipType, user.subscription)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="space-y-1">
                        <div>Messages: {user.messages}</div>
                        <div>Matches: {user.matches}</div>
                        <div className="text-xs">Last: {user.lastActive}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {user.reports > 0 ? (
                        <span className="text-sm text-red-600 font-medium">{user.reports}</span>
                      ) : (
                        <span className="text-sm text-gray-400">0</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleViewDetails(user)}
                          className="text-blue-600 hover:text-blue-900"
                          title="View Details"
                        >
                          <FiEye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            setUserToEdit(user);
                            setShowEditUserModal(true);
                          }}
                          className="text-amber-600 hover:text-amber-900"
                          title="Edit User & Manage Membership"
                        >
                          <FiEdit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDownloadPdf(user._id || user.id)}
                          className="text-purple-600 hover:text-purple-900"
                          title="Download Forensic Data PDF"
                        >
                          <FiDownload className="w-4 h-4" />
                        </button>

                        <button
                          onClick={() => handleUserAction('message', user._id || user.id)}
                          className="text-green-600 hover:text-green-900"
                          title="Send Message"
                        >
                          <FiMail className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleUserAction('suspend', user._id || user.id)}
                          className={`${user.status === 'suspended' ? 'text-green-600 hover:text-green-900' : 'text-yellow-600 hover:text-yellow-900'}`}
                          title={user.status === 'suspended' ? "Unsuspend User" : "Suspend User"}
                        >
                          {user.status === 'suspended' ? (
                            <FiUserCheck className="w-4 h-4" onClick={(e) => { e.stopPropagation(); handleUserAction('unsuspend', user._id || user.id); }} />
                          ) : (
                            <FiUserX className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          onClick={() => handleUserAction('delete', user._id || user.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete User"
                        >
                          <FiTrash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="flex-shrink-0">
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> to <span className="font-medium">{Math.min(currentPage * itemsPerPage, totalUsers)}</span> of <span className="font-medium">{totalUsers}</span> results
                </p>
              </div>
              <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination" style={{ whiteSpace: 'nowrap' }}>
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  {[...Array(totalPages)].map((_, i) => (
                    <button
                      key={i + 1}
                      onClick={() => handlePageChange(i + 1)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === i + 1
                        ? 'z-10 bg-red-50 border-red-500 text-red-600'
                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        </div>

        <ConfirmationModal
          isOpen={confirmationModal.isOpen}
          onClose={() => setConfirmationModal({ ...confirmationModal, isOpen: false })}
          onConfirm={handleConfirmAction}
          title={confirmationModal.title}
          message={confirmationModal.message}
          confirmText={confirmationModal.action === 'message' ? 'Send' : 'Confirm'}
          isDestructive={confirmationModal.action === 'delete'}
          showInput={confirmationModal.action === 'message'}
          inputPlaceholder="Type your message here..."
          inputValue={confirmationModal.inputValue}
          onInputChange={(val) => setConfirmationModal(prev => ({ ...prev, inputValue: val }))}
          isLoading={actionLoading}
        />

        <AddUserModal
          isOpen={showAddUserModal}
          onClose={() => setShowAddUserModal(false)}
          onUserAdded={handleUserAdded}
        />



        {/* User Details Modal */}
        {showUserDetails && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto flex flex-col">
              <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
                <div className="flex items-center space-x-3">
                  <h2 className="text-xl font-bold text-gray-900">User Profile Details</h2>
                  <button
                    onClick={() => handleDownloadPdf(showUserDetails._id || showUserDetails.id)}
                    className="px-3 py-1 bg-purple-100 text-purple-700 rounded-md text-xs font-semibold hover:bg-purple-200 flex items-center gap-1"
                    title="Download Forensic Data PDF"
                  >
                    <FiDownload className="w-3.5 h-3.5" />
                    Download Forensic PDF
                  </button>
                </div>
                <button
                  onClick={() => { setShowUserDetails(null); setDetailedUser(null); }}
                  className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100"
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {loadingDetails ? (
                <div className="p-12 flex justify-center items-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500"></div>
                </div>
              ) : detailedUser ? (
                <div className="p-8">
                  {/* Header Profile Section */}
                  <div className="flex flex-col md:flex-row items-start md:items-center gap-6 mb-8">
                    <div className="relative">
                      {getProfilePhotoUrl(detailedUser.profilePhoto) ? (
                        <img
                          src={getProfilePhotoUrl(detailedUser.profilePhoto)}
                          alt="Profile"
                          className="w-32 h-32 rounded-full object-cover border-4 border-white shadow-lg"
                        />
                      ) : (
                        <div className="w-32 h-32 rounded-full bg-gradient-to-r from-red-500 to-pink-500 flex items-center justify-center text-white font-bold text-4xl border-4 border-white shadow-lg">
                          {detailedUser.firstName?.charAt(0)}{detailedUser.lastName?.charAt(0)}
                        </div>
                      )}

                      {getStatusBadge(detailedUser.status)}
                    </div>

                    <div className="flex-1">
                      <h1 className="text-3xl font-bold text-gray-900 mb-2">
                        {detailedUser.firstName} {detailedUser.lastName}
                        {detailedUser.age && <span className="text-gray-500 font-normal ml-2">({detailedUser.age})</span>}
                        {(!detailedUser.age && detailedUser.dateOfBirth) &&
                          <span className="text-gray-500 font-normal ml-2">({calculateAge(detailedUser.dateOfBirth)})</span>}
                      </h1>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-y-2 gap-x-8 text-sm text-gray-600 mt-4">
                        <div className="flex items-center gap-2">
                          <FiMail className="w-4 h-4 text-gray-400" />
                          <span>{detailedUser.email}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <FiPhone className="w-4 h-4 text-gray-400" />
                          <span>{detailedUser.phone || 'No phone provided'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <FiMapPin className="w-4 h-4 text-gray-400" />
                          <span>{detailedUser.city || detailedUser.country ? `${detailedUser.city || ''}, ${detailedUser.country || ''}` : 'Location unknown'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <FiGlobe className="w-4 h-4 text-gray-400" />
                          <span>{detailedUser.gender ? (detailedUser.gender.charAt(0).toUpperCase() + detailedUser.gender.slice(1)) : 'Gender not specified'}</span>
                        </div>
                      </div>

                      <div className="flex items-center gap-3 mt-4">
                        {getMembershipBadge(detailedUser.membershipType, detailedUser.subscription)}
                        {detailedUser.verified && (
                          <span className="inline-flex items-center px-2 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded-full border border-blue-200">
                            <FiShield className="w-3 h-3 mr-1" />
                            Verified Account
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Tabs / Sections */}
                  <div className="border-b border-gray-200 mb-6"></div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Left Column: Personal Info */}
                    <div className="space-y-6">

                      {/* Subscription & Payment Profile */}
                      <div className="bg-gradient-to-br from-amber-50 to-purple-50 rounded-xl p-6 border border-amber-200/70 shadow-sm">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                            <FiStar className="text-yellow-500 fill-yellow-400" />
                            Membership & Google Play Billing
                          </h3>
                          <button
                            onClick={() => {
                              setUserToEdit(detailedUser);
                              setShowEditUserModal(true);
                            }}
                            className="px-3 py-1 bg-white hover:bg-amber-100 text-amber-800 text-xs font-semibold rounded-lg border border-amber-300 transition flex items-center gap-1 shadow-xs"
                          >
                            <FiEdit className="w-3.5 h-3.5" />
                            Manage
                          </button>
                        </div>

                        <div className="space-y-3 text-sm">
                          <div className="flex justify-between items-center py-1.5 border-b border-amber-100">
                            <span className="text-gray-600 font-medium">Membership Tier</span>
                            <span className="font-bold text-purple-700 uppercase tracking-wide">
                              {detailedUser.membershipType === 'vip' ? '👑 VIP' : (detailedUser.membershipType === 'premium' ? '⭐ Premium' : 'Free Member')}
                            </span>
                          </div>

                          <div className="flex justify-between items-center py-1.5 border-b border-amber-100">
                            <span className="text-gray-600 font-medium">Billing Source</span>
                            <span className="font-semibold text-gray-800 flex items-center gap-1">
                              {detailedUser.subscription?.source === 'google_play' ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 text-xs font-semibold">
                                  📱 Google Play Store
                                </span>
                              ) : (detailedUser.subscription?.source || 'None / Direct')}
                            </span>
                          </div>

                          {detailedUser.subscription?.orderId && (
                            <div className="flex justify-between items-center py-1.5 border-b border-amber-100">
                              <span className="text-gray-600 font-medium">Google Play Order ID</span>
                              <div className="flex items-center gap-1 font-mono text-xs text-gray-800 bg-white px-2 py-0.5 rounded border border-gray-200">
                                <span>{detailedUser.subscription.orderId}</span>
                                <button
                                  type="button"
                                  onClick={() => {
                                    navigator.clipboard.writeText(detailedUser.subscription.orderId);
                                    setCopiedOrderId(true);
                                    setTimeout(() => setCopiedOrderId(false), 2000);
                                  }}
                                  className="text-gray-400 hover:text-gray-700 p-0.5"
                                  title="Copy Order ID"
                                >
                                  {copiedOrderId ? <FiCheck className="w-3.5 h-3.5 text-green-600" /> : <FiCopy className="w-3.5 h-3.5" />}
                                </button>
                              </div>
                            </div>
                          )}

                          <div className="flex justify-between items-center py-1.5 border-b border-amber-100">
                            <span className="text-gray-600 font-medium">Subscription Status</span>
                            <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                              detailedUser.subscription?.status === 'active' 
                                ? 'bg-green-100 text-green-800' 
                                : (detailedUser.subscription?.status === 'expired' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-700')
                            }`}>
                              {detailedUser.subscription?.status ? detailedUser.subscription.status.toUpperCase() : 'FREE'}
                            </span>
                          </div>

                          {detailedUser.subscription?.purchaseDate && (
                            <div className="flex justify-between items-center py-1.5 border-b border-amber-100">
                              <span className="text-gray-600 font-medium">Start Date</span>
                              <span className="text-gray-800 font-medium">
                                {new Date(detailedUser.subscription.purchaseDate).toLocaleDateString()}
                              </span>
                            </div>
                          )}

                          {detailedUser.subscription?.expiryDate && (
                            <div className="flex justify-between items-center py-1.5 border-b border-amber-100">
                              <span className="text-gray-600 font-medium">Expiry / Renewal</span>
                              <span className="text-gray-800 font-medium">
                                {new Date(detailedUser.subscription.expiryDate).toLocaleDateString()}
                              </span>
                            </div>
                          )}

                          {detailedUser.subscription?.notes && (
                            <div className="py-1.5 text-xs text-gray-600 italic bg-white/60 p-2 rounded">
                              Note: {detailedUser.subscription.notes}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* About Me */}
                      <div className="bg-gray-50 rounded-xl p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                          <FiUsers className="text-red-500" />
                          About
                        </h3>
                        <p className="text-gray-700 leading-relaxed italic">
                          "{detailedUser.aboutMe || 'No bio provided.'}"
                        </p>
                      </div>

                      {/* Personal Details */}
                      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                          <FiBriefcase className="text-blue-500" />
                          Personal & Professional
                        </h3>
                        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-4">
                          <div>
                            <dt className="text-xs text-gray-500 uppercase tracking-wide">Education</dt>
                            <dd className="text-sm font-medium text-gray-900 mt-1">{detailedUser.education || 'Not specified'}</dd>
                          </div>
                          <div>
                            <dt className="text-xs text-gray-500 uppercase tracking-wide">Profession</dt>
                            <dd className="text-sm font-medium text-gray-900 mt-1">{detailedUser.profession || 'Not specified'}</dd>
                          </div>

                          <div>
                            <dt className="text-xs text-gray-500 uppercase tracking-wide">Religion</dt>
                            <dd className="text-sm font-medium text-gray-900 mt-1">{detailedUser.religion || 'Not specified'}</dd>
                          </div>
                          <div>
                            <dt className="text-xs text-gray-500 uppercase tracking-wide">Community</dt>
                            <dd className="text-sm font-medium text-gray-900 mt-1">{detailedUser.community || 'Not specified'}</dd>
                          </div>
                          <div>
                            <dt className="text-xs text-gray-500 uppercase tracking-wide">Marital Status</dt>
                            <dd className="text-sm font-medium text-gray-900 mt-1">{detailedUser.maritalStatus || 'Not specified'}</dd>
                          </div>
                        </dl>
                      </div>

                      {/* Interests */}
                      {detailedUser.interests && detailedUser.interests.length > 0 && (
                        <div>
                          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Interests</h3>
                          <div className="flex flex-wrap gap-2">
                            {detailedUser.interests.map((interest, idx) => (
                              <span key={idx} className="px-3 py-1 bg-red-50 text-red-700 text-sm rounded-full border border-red-100">
                                {interest}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Right Column: Platform Activity & Meta */}
                    <div className="space-y-6">
                      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                          <FiCalendar className="text-purple-500" />
                          Account Activity
                        </h3>
                        <div className="space-y-4">
                          <div className="flex justify-between items-center py-2 border-b border-gray-100">
                            <span className="text-gray-600">Registered On</span>
                            <span className="font-medium text-gray-900">
                              {detailedUser.createdAt ? new Date(detailedUser.createdAt).toLocaleDateString() : 'Unknown'}
                            </span>
                          </div>
                          <div className="flex justify-between items-center py-2 border-b border-gray-100">
                            <span className="text-gray-600">Last Active</span>
                            <span className="font-medium text-gray-900">{detailedUser.lastActive || 'Unknown'}</span>
                          </div>
                          <div className="flex justify-between items-center py-2 border-b border-gray-100">
                            <span className="text-gray-600">Total Messages Sent</span>
                            <span className="font-medium text-gray-900">{Array.isArray(detailedUser.messages) ? detailedUser.messages.length : (detailedUser.messages || 0)}</span>
                          </div>
                          <div className="flex justify-between items-center py-2 border-b border-gray-100">
                            <span className="text-gray-600">Profile Matches</span>
                            <span className="font-medium text-gray-900">{Array.isArray(detailedUser.matches) ? detailedUser.matches.length : (detailedUser.matches || 0)}</span>
                          </div>
                          <div className="flex justify-between items-center py-2 border-b border-gray-100">
                            <span className="text-gray-600">Reports Received</span>
                            <span className="font-medium text-red-600">{Array.isArray(detailedUser.reports) ? detailedUser.reports.length : (detailedUser.reports || 0)}</span>
                          </div>
                        </div>
                      </div>

                      {/* Admin Actions */}
                      <div className="bg-gray-50 rounded-xl p-6">
                        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Quick Actions</h3>
                        <div className="grid grid-cols-2 gap-3">
                          <button
                            onClick={() => {
                              setUserToEdit(detailedUser);
                              setShowEditUserModal(true);
                            }}
                            className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm font-medium flex items-center justify-center gap-1.5 shadow-sm"
                          >
                            <FiEdit className="w-4 h-4" />
                            Edit Membership
                          </button>
                          <button
                            onClick={() => { setShowUserDetails(null); handleUserAction('message', detailedUser._id || detailedUser.id); }}
                            className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium"
                          >
                            Send Message
                          </button>
                          <button
                            onClick={() => { setShowUserDetails(null); handleUserAction('suspend', detailedUser._id || detailedUser.id); }}
                            className="px-4 py-2 bg-yellow-50 border border-yellow-200 text-yellow-700 rounded-lg hover:bg-yellow-100 text-sm font-medium"
                          >
                            {detailedUser.status === 'suspended' ? 'Unsuspend' : 'Suspend User'}
                          </button>
                          <button
                            onClick={() => { setShowUserDetails(null); handleUserAction('delete', detailedUser._id || detailedUser.id); }}
                            className="px-4 py-2 bg-red-50 border border-red-200 text-red-700 rounded-lg hover:bg-red-100 text-sm font-medium"
                          >
                            Delete User
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-12 text-center text-gray-500">
                  <FiUserX className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <p>User details not found.</p>
                </div>
              )}
            </div>
          </div>
        )}

        <EditUserModal
          isOpen={showEditUserModal}
          user={userToEdit}
          onClose={() => {
            setShowEditUserModal(false);
            setUserToEdit(null);
          }}
          onUserUpdated={(updatedUser) => {
            fetchUsers();
            fetchUserStats();
            if (detailedUser && (detailedUser._id === updatedUser._id || detailedUser.id === updatedUser._id)) {
              setDetailedUser(prev => ({ ...prev, ...updatedUser }));
            }
            success('User & Membership updated successfully!');
          }}
        />
      </div>
    </AdminLayout>
  );
};

export default UserManagement;
